import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { createBeforeAfterComposite, createSingleImageComposite } from '../utils/imageCompositor';
import { uploadShareImage, createShareRecord } from '../utils/firebase';
import { trackEvent } from '../utils/analytics';

function SocialShareDialog({ show, onHide, image, currentStateImages = [], sessionTitle, uid, sessionId }) {
  const [step, setStep] = useState(1);
  const [selectedBefore, setSelectedBefore] = useState(null);
  const [layout, setLayout] = useState('side-by-side');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [compositeBlob, setCompositeBlob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (show) {
      setStep(currentStateImages.length > 0 ? 1 : 2);
      setSelectedBefore(currentStateImages.length === 1 ? currentStateImages[0] : null);
      setLayout('side-by-side');
      setPreviewUrl(null);
      setCompositeBlob(null);
      setGenerating(false);
      setSharing(false);
      setError('');
      setCopied(false);
      setShareUrl(null);
      trackEvent('social_share_opened');
    }
  }, [show, currentStateImages]);

  // Generate composite when moving to step 2
  const generatePreview = useCallback(async () => {
    if (!image) return;
    setGenerating(true);
    setError('');
    try {
      let result;
      if (selectedBefore) {
        result = await createBeforeAfterComposite(selectedBefore.url, image.url, { layout });
      } else {
        result = await createSingleImageComposite(image.url, { layout });
      }
      setPreviewUrl(result.dataUrl);
      setCompositeBlob(result.blob);
      trackEvent('social_share_composite_created', { has_before_image: !!selectedBefore, layout });
    } catch (err) {
      console.error('Failed to generate composite:', err);
      setError('Failed to generate image. The source images may not be accessible.');
    } finally {
      setGenerating(false);
    }
  }, [image, selectedBefore, layout]);

  // Regenerate preview when layout changes on step 2
  useEffect(() => {
    if (step === 2 && image) {
      generatePreview();
    }
  }, [step, layout, generatePreview, image]);

  const handleSelectBefore = (img) => {
    setSelectedBefore(prev => prev?.id === img.id ? null : img);
  };

  const handleSkipBefore = () => {
    setSelectedBefore(null);
    setStep(2);
  };

  const handleNextToPreview = () => {
    setStep(2);
  };

  const handleBackToSelect = () => {
    setStep(1);
    setPreviewUrl(null);
    setCompositeBlob(null);
  };

  const handleDownload = () => {
    if (!compositeBlob) return;
    const url = URL.createObjectURL(compositeBlob);
    const a = document.createElement('a');
    a.href = url;
    const name = image?.title || image?.name || 'renovation-design';
    const safeName = name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').toLowerCase();
    a.download = `${safeName}-share.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackEvent('social_share_downloaded', { layout });
  };

  const getOrCreateShareUrl = async () => {
    if (shareUrl) return shareUrl;
    setSharing(true);
    setError('');
    try {
      // Create share record first to get the ID
      const shareId = await createShareRecord(uid, sessionId, {
        imageUrl: '', // placeholder, will update after upload
        sessionTitle: sessionTitle || null,
        designTitle: image?.title || image?.name || null,
        roomType: image?.room_type || null,
      });

      // Upload composite image
      const imageUrl = await uploadShareImage(uid, shareId, compositeBlob);

      // Update the share record with the actual image URL
      const { database } = await import('../utils/firebase');
      const { ref, update } = await import('firebase/database');
      await update(ref(database, `publicShares/${shareId}`), { imageUrl });

      const host = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://www.plum-app.com';
      const url = `${host}/s/${shareId}`;
      setShareUrl(url);
      return url;
    } catch (err) {
      console.error('Failed to create share link:', err);
      setError('Failed to create share link. Please try again.');
      return null;
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    const url = await getOrCreateShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      trackEvent('social_share_link_copied', { layout });
    } catch (err) {
      setError('Failed to copy link to clipboard.');
    }
  };

  const handleWebShareApi = async () => {
    if (!compositeBlob) return;
    const name = image?.title || image?.name || 'renovation-design';
    const file = new File([compositeBlob], `${name}.png`, { type: 'image/png' });
    const shareData = {
      files: [file],
      title: image?.title || 'Renovation Design',
      text: 'Check out this renovation design I made with Plum!',
    };
    try {
      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        trackEvent('social_share_web_share_api', { layout });
      }
    } catch (err) {
      // User cancelled share - not an error
      if (err.name !== 'AbortError') {
        console.error('Web Share API error:', err);
      }
    }
  };

  const canWebShare = typeof navigator !== 'undefined' && navigator.canShare?.({
    files: [new File([''], 'test.png', { type: 'image/png' })],
  });

  if (!image) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="social-share-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-share me-2"></i>
          Share Design
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {/* Step 1: Select Before Image */}
        {step === 1 && (
          <div>
            <p className="text-muted mb-3">
              Select a "before" photo to create a side-by-side comparison, or skip to share just the design.
            </p>
            <Row className="g-2 before-image-selector">
              {currentStateImages.map((img) => (
                <Col xs={4} sm={3} key={img.id}>
                  <div
                    className={`before-image-option ${selectedBefore?.id === img.id ? 'selected' : ''}`}
                    onClick={() => handleSelectBefore(img)}
                  >
                    <img
                      src={img.url}
                      alt={img.summary || 'Current state'}
                      className="w-100 rounded"
                      style={{ aspectRatio: '1', objectFit: 'cover' }}
                    />
                    {selectedBefore?.id === img.id && (
                      <div className="before-image-check">
                        <i className="bi bi-check-circle-fill"></i>
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
            <div className="d-flex gap-2 mt-3 justify-content-end">
              <Button variant="outline-secondary" size="sm" onClick={handleSkipBefore}>
                Skip - Share design only
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleNextToPreview}
                disabled={!selectedBefore}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview & Layout */}
        {step === 2 && (
          <div>
            {/* Layout toggle - only show when before image is selected */}
            {selectedBefore && (
              <div className="d-flex gap-2 mb-3 justify-content-center">
                <Button
                  variant={layout === 'side-by-side' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setLayout('side-by-side')}
                >
                  <i className="bi bi-layout-split me-1"></i>
                  Side by Side
                </Button>
                <Button
                  variant={layout === 'stacked' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setLayout('stacked')}
                >
                  <i className="bi bi-layout-three-columns me-1" style={{ transform: 'rotate(90deg)', display: 'inline-block' }}></i>
                  Stacked
                </Button>
              </div>
            )}

            {/* Preview */}
            <div className="text-center mb-3">
              {generating ? (
                <div className="py-5">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Generating preview...
                </div>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Share preview"
                  className="social-share-preview img-fluid"
                />
              ) : null}
            </div>

            {/* Share Actions */}
            {!generating && previewUrl && (
              <div className="share-actions">
                <div className="d-flex flex-column gap-2">
                  {/* Download */}
                  <Button
                    variant="outline-secondary"
                    className="share-platform-btn"
                    onClick={handleDownload}
                  >
                    <i className="bi bi-download"></i>
                    <span>Download Image</span>
                  </Button>

                  {/* Copy Link */}
                  <Button
                    variant="outline-secondary"
                    className="share-platform-btn"
                    onClick={handleCopyLink}
                    disabled={sharing}
                  >
                    {sharing ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className={`bi ${copied ? 'bi-check-lg' : 'bi-link-45deg'}`}></i>
                    )}
                    <span>{copied ? 'Link Copied!' : 'Copy Share Link'}</span>
                  </Button>

                  {/* Web Share API (mobile) */}
                  {canWebShare && (
                    <Button
                      variant="primary"
                      className="share-platform-btn"
                      onClick={handleWebShareApi}
                    >
                      <i className="bi bi-share"></i>
                      <span>Share via Apps</span>
                      <small className="text-muted ms-auto">Instagram, Threads, Snapchat...</small>
                    </Button>
                  )}

                  {/* Hint for desktop users */}
                  {!canWebShare && (
                    <small className="text-muted text-center mt-1">
                      <i className="bi bi-phone me-1"></i>
                      On mobile, you can share directly to Instagram, Threads, and Snapchat
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Back button if we came from step 1 */}
            {currentStateImages.length > 0 && (
              <div className="mt-3">
                <Button variant="link" size="sm" onClick={handleBackToSelect} className="text-muted p-0">
                  <i className="bi bi-arrow-left me-1"></i>Change before photo
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default SocialShareDialog;
